import React, { useEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getTagsList, saveTagOrder } from "../services/journalService";
import Loader from "./common/loader";

const CLICK_DISTANCE_THRESHOLD = 5;

const TagPill = styled.div`
  display: inline-block;
  background-color: ${({ theme }) => theme.card};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: 20px;
  padding: 0.4rem 1rem;
  margin: 0.3rem;
  color: inherit;
  cursor: grab;
  touch-action: none;

  &:active {
    cursor: grabbing;
  }
`;

const SortableTagPill = ({ tag, count, onNavigate }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: tag });
  const pointerDownPos = useRef(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Rather than trying to suppress whatever native "click" the browser may
  // or may not fire after a drag release (timing-dependent and unreliable
  // across real mouse/trackpad input), measure the actual distance between
  // press and release ourselves and only navigate when it was negligible -
  // i.e. a real click, not a drag.
  const handlePointerDown = e => {
    pointerDownPos.current = { x: e.clientX, y: e.clientY };
    if (listeners.onPointerDown) listeners.onPointerDown(e);
  };

  const handlePointerUp = e => {
    const start = pointerDownPos.current;
    pointerDownPos.current = null;
    if (!start) return;
    const distance = Math.hypot(e.clientX - start.x, e.clientY - start.y);
    if (distance < CLICK_DISTANCE_THRESHOLD) onNavigate(tag);
  };

  return (
    <TagPill
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
    >
      #{tag} <span className="text-muted">{count}</span>
    </TagPill>
  );
};

const Tags = () => {
  const [tags, setTags] = useState(null);
  const history = useHistory();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    async function fetchTags() {
      const { data } = await getTagsList();
      setTags(data);
    }
    fetchTags();
  }, []);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    setTags(current => {
      const oldIndex = current.findIndex(t => t.tag === active.id);
      const newIndex = current.findIndex(t => t.tag === over.id);
      const reordered = arrayMove(current, oldIndex, newIndex);
      saveTagOrder(reordered.map(t => t.tag));
      return reordered;
    });
  };

  const handleNavigate = tag => history.push(`/tags/${tag}`);

  if (!tags) return <Loader fontSize="4rem" />;

  return (
    <Container className="mt-2">
      <h4 className="mb-3">Tags</h4>
      {tags.length === 0 && (
        <p className="text-muted">
          No tags yet — add #hashtags to your entries.
        </p>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={tags.map(t => t.tag)}
          strategy={rectSortingStrategy}
        >
          {tags.map(({ tag, count }) => (
            <SortableTagPill
              key={tag}
              tag={tag}
              count={count}
              onNavigate={handleNavigate}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Container>
  );
};

export default Tags;
